/**
 * The one way out of a session (brief rule 5): tell the API first
 * (`DELETE /auth/tokens` invalidates the server-side session, so a copied
 * cookie stops working), clear local state, then land on the login page
 * with the hash that picks the message there (`useLogoutHash`).
 *
 * The hash is also left in localStorage before the user slice flips
 * `isLogin` to false, so the other tabs of the session, which learn about
 * the logout from that `storage` event, can leave with the same message.
 *
 * The redirect never waits on the API for long. A background tab that
 * the browser froze (Chrome after minutes, Safari too) can lose the
 * request mid-flight, and a promise that never settles would have kept
 * the dashboard on screen with the session long over - which is what
 * happened overnight. The DELETE is sent with keepalive and the page
 * leaves after at most SERVER_LOGOUT_WAIT_MS whatever the API said.
 */
import { endSession } from 'redux_store';
import { clearIdleStore, writeLogoutHash } from '@shared/lib/idleSession';

export const SERVER_LOGOUT_WAIT_MS = 2000;

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

export const LOGOUT_HASH = {
  // Idle timeout reached, countdown ran out, or the server window closed.
  auto: '#auto_logout',
  // The user chose "Log out" in the idle dialog.
  manual: '#logout_without_notification',
};

let inFlight = null;

export const logoutSession = (dispatch, hash = LOGOUT_HASH.auto) => {
  if (inFlight) return inFlight;
  inFlight = (async () => {
    writeLogoutHash(hash);
    try {
      await Promise.race([dispatch(endSession()), wait(SERVER_LOGOUT_WAIT_MS)]);
    } finally {
      clearIdleStore();
      window.location.assign(`/login${hash}`);
      // The page is leaving. If it is still here later (the navigation
      // was lost with a frozen tab), the hook asks again and this must
      // not answer with a promise that already did its work.
      inFlight = null;
    }
  })();
  return inFlight;
};
